import { Component, computed, inject, input, output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import type { FileUpload } from 'primeng/fileupload';
import type { FileSelectEvent } from 'primeng/types/fileupload';
import { PrimeNGModule } from '@shared/primeng/primeng.module';
import { PrimeNG } from 'primeng/config';

@Component({
  selector: 'kl-upload-file-advanced',
  standalone: true,
  imports: [TranslateModule, PrimeNGModule],
  templateUrl: './kl-upload-file-advanced.html',
  styleUrl: './kl-upload-file-advanced.scss',
})
export class KlUploadFileAdvancedComponent {
  @ViewChild('fileUpload', { static: false }) fileUpload!: FileUpload;

  private readonly primeConfig = inject(PrimeNG);

  readonly showFileEvent = output<void>();
  readonly onSelectedFileEvent = output<FileSelectEvent>();
  readonly onClearFileEvent = output<void>();

  readonly disabled = input(false);
  readonly maxFileSize = input(10);
  readonly acceptedFileExtentions = input('.pdf,.rar,.docx,image/*');
  readonly invalidFileTypeMessageSummary = input('Geçersiz belge türü: ');
  readonly invalidFileTypeMessageDetail = input('');
  readonly invalidFileTypeMessageDetailResolved = computed(() => {
    const detail = this.invalidFileTypeMessageDetail();
    return detail || 'İzin verilen dosya türleri: ' + this.acceptedFileExtentions();
  });
  readonly multiple = input(false);
  readonly fileContent = input('');
  readonly fileName = input('');
  readonly fileType = input('');
  readonly fileSize = input(0);

  readonly hasFile = computed(() => !!(this.fileContent() || this.fileName()));

  readonly fileExtension = computed(() => {
    const name = this.fileName();
    if (!name) return '';
    const dotIdx = name.lastIndexOf('.');
    return dotIdx >= 0 ? name.substring(dotIdx + 1).toLowerCase() : '';
  });

  readonly fileTypeIconClass = computed(() => {
    const ext = this.fileExtension();
    const mime = this.fileType();

    if (mime.startsWith('image/')) return 'ph-image';
    switch (ext) {
      case 'xls':
      case 'xlsx':
      case 'xlsm':
        return 'ph-file-xls';
      case 'pdf':
        return 'ph-file-pdf';
      case 'doc':
      case 'docx':
        return 'ph-file-doc';
      case 'csv':
        return 'ph-file-csv';
      case 'zip':
      case 'rar':
      case '7z':
        return 'ph-file-zip';
      default:
        return 'ph-file';
    }
  });

  readonly canPreview = computed(() => {
    const mime = this.fileType();
    if (mime.startsWith('image/') || mime === 'application/pdf') return true;
    const ext = this.fileExtension();
    return ext === 'pdf' || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  });

  readonly sizeLabel = computed(() => {
    const bytes = this.fileSize();
    if (!bytes || bytes <= 0) return '';
    return this.formatSize(bytes);
  });

  readonly helperText = computed(() => {
    const accepted = this.acceptedFileExtentions();
    const max = this.maxFileSize();
    const parts: string[] = [];
    if (accepted) parts.push(accepted);
    if (max) parts.push(`Max ${max} MB`);
    return parts.join('  •  ');
  });

  choose(_event: Event, callback: () => void): void {
    callback();
  }

  clear(clearCallback: () => void): void {
    clearCallback();
    this.onClearFileEvent.emit();
  }

  /** Clears PrimeNG file upload state (for parent-driven resets). */
  clearUpload(): void {
    this.fileUpload?.clear();
  }

  private formatSize(bytes: number): string {
    const k = 1024;
    const dm = 3;
    const sizes = this.primeConfig.translation.fileSizeTypes;
    if (!sizes || sizes.length === 0) return '0 B';
    if (bytes === 0) return `0 ${sizes[0]}`;

    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
    const unit = sizes[Math.max(0, i)] ?? 'B';
    return `${formattedSize} ${unit}`;
  }
}
